/**
 * Firebase Realtime Database Compatibility Layer
 *
 * Maps Firebase RTDB API calls to the Cloudflare Workers REST API.
 * Components keep their existing Firebase API patterns (ref, get, onValue, set, update, remove, push, etc.)
 * but the operations now hit the API Worker at REACT_APP_API_URL.
 *
 * Realtime is implemented as polling (5s interval) for the few collections that need it.
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api-client';
import { rewriteImagesInObject } from './image-url-rewriter';

const MAX = 100;
const POLL_INTERVAL_MS = 5000;

// ─── ID generation ──────────────────────────────────────────────────
function uniqueId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── JSON helpers ───────────────────────────────────────────────────
function jp(val, fallback = null) {
  if (val == null) return fallback;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// ─── Collection / route mapping ─────────────────────────────────────
// Firebase root path → API Worker route segment.
const ROUTE_MAP = {
  villas: 'villas',
  users: 'users',
  bookings: 'bookings',
  allBookings: 'bookings',
  reviews: 'reviews',
  wishlists: 'wishlists',
  queries: 'queries',
  userQueries: 'user-queries',
  blogs: 'blogs',
  cityBlogs: 'blogs',
  destinations: 'destinations',
  promotedVillas: 'promoted-villas',
  specialOccasions: 'special-occasions',
  analytics: 'analytics',
  cancellationRequests: 'cancellations',
  luxuryVillas: 'luxury-villas',
  luxuryShuffleData: 'luxury-shuffle',
  notifications: '_disabled_notifications',
};

// Fields that may be JSON-encoded strings on the wire and need parsing
const VILLA_JSON_FIELDS = [
  'pricing', 'facilities', 'images', 'locationHighlights', 'management',
  'ownerInfo', 'location', 'meals', 'rules', 'locationImages', 'youtubeShorts', 'spaces',
];
const ARRAY_JSON_FIELDS = ['facilities', 'images', 'locationHighlights', 'rules', 'locationImages', 'youtubeShorts'];

// Path segments that map to a query field
const QUERY_FIELD_MAP = {
  bookings: 'userId',
  wishlists: 'userId',
  notifications: 'userId',
  reviews: 'villaId',
};

// ─── Document parsing ───────────────────────────────────────────────

function parseDocument(doc, rootName) {
  if (!doc) return null;
  const data = { ...doc };

  // Strip API metadata if present
  delete data.$id;
  delete data.$collectionId;
  delete data.$databaseId;
  delete data.$createdAt;
  delete data.$updatedAt;
  delete data.$permissions;

  if (rootName === 'villas') {
    for (const f of VILLA_JSON_FIELDS) {
      if (typeof data[f] === 'string') {
        data[f] = jp(data[f], ARRAY_JSON_FIELDS.includes(f) ? [] : null);
      }
    }
    if (data.pricing && typeof data.pricing === 'object') {
      if (data.discount === undefined && data.pricing.discount !== undefined) data.discount = data.pricing.discount;
      if (data.offerValidTill === undefined && data.pricing.offerValidTill !== undefined) data.offerValidTill = data.pricing.offerValidTill;
      if (data.offerMessage === undefined && data.pricing.offerMessage !== undefined) data.offerMessage = data.pricing.offerMessage;
      if (data.termsAndConditions === undefined && data.pricing.termsAndConditions !== undefined) data.termsAndConditions = data.pricing.termsAndConditions;
      if (data.isFeaturedOffer === undefined && data.pricing.isFeaturedOffer !== undefined) data.isFeaturedOffer = data.pricing.isFeaturedOffer;
    }
    if (data.ownerInfo !== undefined) {
      data.owner = data.ownerInfo;
      delete data.ownerInfo;
    }

    // Build the effective `images` gallery by combining all image-bearing fields.
    // The source data only stores the hero image in `images`; rooms/spaces have the rest.
    data.images = buildVillaGallery(data);
  }

  if (typeof data.villaData === 'string') data.villaData = jp(data.villaData, {});
  if (typeof data.bookingData === 'string') data.bookingData = jp(data.bookingData, {});
  if (typeof data.reply === 'string') data.reply = jp(data.reply, null);
  if (typeof data.replies === 'string') data.replies = jp(data.replies, {});
  if (typeof data.shuffledOrder === 'string') data.shuffledOrder = jp(data.shuffledOrder, []);
  if (typeof data.fcmTokens === 'string') data.fcmTokens = jp(data.fcmTokens, {});

  if (rootName !== 'villas') {
    if (typeof data.images === 'string') data.images = jp(data.images, []);
    if (typeof data.facilities === 'string') data.facilities = jp(data.facilities, []);
  }

  // Translate legacy Firebase/Appwrite image URLs to R2
  return rewriteImagesInObject(data);
}

function getDocId(doc) {
  return doc?.id || doc?.$id;
}

/**
 * Combine the various image-bearing fields on a villa into one ordered gallery.
 * Original schema only stores the hero image in `images`; the actual gallery
 * lives in `spaces[].image`, `locationImages`, and `meals.images`.
 */
function buildVillaGallery(villa) {
  const gallery = [];
  const seen = new Set();

  const push = (url) => {
    if (typeof url !== 'string' || !url) return;
    if (seen.has(url)) return;
    seen.add(url);
    gallery.push(url);
  };

  // 1. Original `images` field (hero) — could be array, single string, or stringified JSON
  let primary = villa.images;
  if (typeof primary === 'string') {
    const trimmed = primary.trim();
    if (trimmed.startsWith('[')) {
      primary = jp(trimmed, [trimmed]);
    } else {
      primary = [trimmed];
    }
  }
  if (Array.isArray(primary)) primary.forEach(push);

  // 2. Thumbnail (always promote to gallery if not already there)
  if (typeof villa.thumbnail === 'string') push(villa.thumbnail);

  // 3. Rooms / spaces — each space has an `image` field
  if (Array.isArray(villa.spaces)) {
    for (const s of villa.spaces) {
      if (s && typeof s.image === 'string') push(s.image);
    }
  }

  // 4. Location images
  if (Array.isArray(villa.locationImages)) {
    villa.locationImages.forEach(push);
  }

  // 5. Meal images
  if (villa.meals && Array.isArray(villa.meals.images)) {
    villa.meals.images.forEach(push);
  }

  return gallery;
}

// ─── DataSnapshot ───────────────────────────────────────────────────

class DataSnapshot {
  constructor(data, key, exists = true) {
    this._data = data;
    this._key = key;
    this._exists = exists && data !== null && data !== undefined;
  }
  val() { return this._data; }
  exists() { return this._exists; }
  get key() { return this._key; }
  forEach(callback) {
    if (this._data && typeof this._data === 'object') {
      for (const [k, v] of Object.entries(this._data)) {
        const childSnap = new DataSnapshot(v, k);
        const shouldStop = callback(childSnap);
        if (shouldStop === true) break;
      }
    }
  }
  child(path) {
    const val = this._data ? this._data[path] : null;
    return new DataSnapshot(val, path, val != null);
  }
  get ref() { return { key: this._key }; }
}

// ─── DatabaseRef ────────────────────────────────────────────────────

class DatabaseRef {
  constructor(path, queryConstraints = []) {
    this.path = String(path || '').replace(/^\/+|\/+$/g, '');
    this.parts = this.path.split('/').filter(Boolean);
    this._queryConstraints = queryConstraints;
    this._resolve();
  }

  get key() {
    return this.parts[this.parts.length - 1] || null;
  }

  _resolve() {
    const root = this.parts[0];
    this.rootName = root;
    this.route = ROUTE_MAP[root] || root;
    this.docId = null;
    this.filters = {};
    this.fieldPath = null;
    this.isWishlistEntry = false;
    this.isAnalytics = false;

    if (root === 'analytics') {
      this.isAnalytics = true;
      if (this.parts.length >= 3) {
        this.analyticsType = this.parts[1];
        this.analyticsVillaId = this.parts[2];
      }
      return;
    }

    if (root === 'luxuryShuffleData') {
      this.docId = 'shuffle-state';
      return;
    }

    if (root === 'allBookings') {
      if (this.parts.length >= 2) {
        this.filters.bookingId = this.parts[1];
        this._isAllBookingsLookup = true;
      }
      return;
    }

    if (root === 'cityBlogs') {
      if (this.parts.length >= 2) {
        this.filters.cityKey = this.parts[1];
      }
      if (this.parts.length >= 3) {
        this.docId = this.parts[2];
        this.filters = {};
      }
      return;
    }

    if (this.parts.length === 1) {
      // Collection-level: list all
      return;
    }

    if (this.parts.length === 2) {
      if (QUERY_FIELD_MAP[root]) {
        this.filters[QUERY_FIELD_MAP[root]] = this.parts[1];
      } else {
        this.docId = this.parts[1];
      }
      return;
    }

    if (this.parts.length === 3) {
      if (root === 'wishlists') {
        this.isWishlistEntry = true;
        this.wishlistUserId = this.parts[1];
        this.wishlistVillaId = this.parts[2];
        return;
      }
      if (root === 'villas' && this.parts[2] === 'reviews') {
        this.route = 'reviews';
        this.filters.villaId = this.parts[1];
        return;
      }
      this.docId = this.parts[1];
      this.fieldPath = this.parts.slice(2).join('/');
      if (QUERY_FIELD_MAP[root]) {
        this.docId = null;
        this.filters[QUERY_FIELD_MAP[root]] = this.parts[1];
        this._specificChildId = this.parts[2];
        this.fieldPath = null;
      }
      return;
    }

    if (this.parts.length >= 4) {
      if (root === 'villas' && this.parts[2] === 'reviews') {
        this.route = 'reviews';
        this.docId = this.parts[3];
        return;
      }
      if (root === 'users') {
        this.docId = this.parts[1];
        this.fieldPath = this.parts.slice(2).join('/');
        return;
      }
      if (root === 'reviews') {
        this.docId = this.parts[2];
        return;
      }
      if (root === 'bookings' && this.parts.length === 4) {
        this.filters.userId = this.parts[1];
        this._specificChildId = this.parts[2];
        this.fieldPath = this.parts[3];
        return;
      }
    }
  }
}

// ─── Public API ─────────────────────────────────────────────────────

export function ref(_db, path) {
  return new DatabaseRef(path || '');
}

export function query(refObj, ...constraints) {
  return new DatabaseRef(refObj.path, constraints);
}

export function orderByChild(field) {
  return { _type: 'orderBy', field };
}

export function equalTo(value) {
  return { _type: 'equalTo', value, _getValue: () => value };
}

export function limitToLast(n) {
  return { _type: 'limitToLast', _limit: n };
}

export function limitToFirst(n) {
  return { _type: 'limitToFirst', _limit: n };
}

export function startAt() {
  return { _type: 'startAt' };
}

export function endAt() {
  return { _type: 'endAt' };
}

// Resolve constraints into API query params
function buildParams(refObj) {
  const params = { ...refObj.filters };
  let limit;
  let orderField;
  let orderDir = 'ASC';

  for (const c of refObj._queryConstraints || []) {
    if (c._type === 'orderBy') orderField = c.field;
    if (c._type === 'equalTo' && orderField) params[orderField] = c._getValue();
    if (c._type === 'limitToFirst' || c._type === 'limitToLast') limit = c._limit;
    if (c._type === 'limitToLast') orderDir = 'DESC';
  }

  if (orderField) {
    params.orderBy = orderField;
    params.orderDir = orderDir;
  }
  params.limit = limit || MAX;
  return params;
}

// ─── Fetch helpers ──────────────────────────────────────────────────

async function fetchRef(refObj) {
  // Notifications collection was excluded from migration — return empty immediately
  if (refObj.rootName === 'notifications') {
    return new DataSnapshot(null, refObj.key, false);
  }
  if (refObj.isAnalytics) return await fetchAnalytics(refObj);
  if (refObj.isWishlistEntry) return await fetchWishlistEntry(refObj);

  // Single document by ID
  if (refObj.docId) {
    try {
      const doc = await apiGet(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`);
      const data = parseDocument(doc, refObj.rootName);
      if (refObj.fieldPath) {
        const field = refObj.fieldPath.split('/')[0];
        return new DataSnapshot(data?.[field], refObj.key, data?.[field] !== undefined);
      }
      return new DataSnapshot(data, getDocId(doc) || refObj.key, true);
    } catch {
      return new DataSnapshot(null, refObj.key, false);
    }
  }

  // List / query
  try {
    const params = buildParams(refObj);
    const res = await apiGet(`/${refObj.route}`, params);
    const docs = res?.documents || [];

    // wishlists/{userId} → return as { villaId: true } map
    if (refObj.rootName === 'wishlists' && refObj.parts.length === 2) {
      if (docs.length === 0) return new DataSnapshot(null, refObj.key, false);
      const map = {};
      for (const d of docs) {
        const data = parseDocument(d, 'wishlists');
        if (data?.villaId) map[data.villaId] = true;
      }
      return new DataSnapshot(Object.keys(map).length ? map : null, refObj.key, Object.keys(map).length > 0);
    }

    if (refObj._specificChildId) {
      const found = docs.find(d => getDocId(d) === refObj._specificChildId || d.bookingId === refObj._specificChildId);
      if (found) {
        const data = parseDocument(found, refObj.rootName);
        if (refObj.fieldPath) {
          const field = refObj.fieldPath.split('/')[0];
          return new DataSnapshot(data?.[field], refObj._specificChildId);
        }
        return new DataSnapshot(data, getDocId(found), true);
      }
      return new DataSnapshot(null, refObj._specificChildId, false);
    }

    if (refObj._isAllBookingsLookup) {
      if (docs.length > 0) {
        const d = docs[0];
        return new DataSnapshot(parseDocument(d, refObj.rootName), getDocId(d), true);
      }
      return new DataSnapshot(null, refObj.key, false);
    }

    if (docs.length === 0) return new DataSnapshot(null, refObj.key, false);

    // Firebase format: object keyed by document ID
    const obj = {};
    for (const d of docs) {
      const id = getDocId(d);
      if (id) obj[id] = parseDocument(d, refObj.rootName);
    }
    return new DataSnapshot(obj, refObj.key, true);
  } catch (err) {
    console.error('fetchRef error:', refObj.path, err);
    return new DataSnapshot(null, refObj.key, false);
  }
}

async function fetchAnalytics(refObj) {
  if (!refObj.analyticsType || !refObj.analyticsVillaId) {
    try {
      const res = await apiGet('/analytics', { limit: MAX });
      const docs = res?.documents || [];
      const obj = {};
      for (const d of docs) {
        const key = `${d.type}/${d.villaId}`;
        obj[key] = d;
      }
      return new DataSnapshot(obj, 'analytics', Object.keys(obj).length > 0);
    } catch {
      return new DataSnapshot(null, 'analytics', false);
    }
  }

  try {
    const res = await apiGet('/analytics', {
      villaId: refObj.analyticsVillaId,
      type: refObj.analyticsType,
      limit: 1,
    });
    const docs = res?.documents || [];
    if (docs.length === 0) return new DataSnapshot(null, refObj.analyticsVillaId, false);
    const d = docs[0];
    if (refObj.analyticsType !== 'visitors') {
      return new DataSnapshot(d.count || 0, refObj.analyticsVillaId, true);
    }
    return new DataSnapshot(d, refObj.analyticsVillaId, true);
  } catch {
    return new DataSnapshot(null, refObj.analyticsVillaId, false);
  }
}

async function fetchWishlistEntry(refObj) {
  try {
    const res = await apiGet('/wishlists', {
      userId: refObj.wishlistUserId,
      villaId: refObj.wishlistVillaId,
      limit: 1,
    });
    const docs = res?.documents || [];
    return new DataSnapshot(docs.length > 0 ? true : null, refObj.wishlistVillaId, docs.length > 0);
  } catch {
    return new DataSnapshot(null, refObj.wishlistVillaId, false);
  }
}

// ─── get ────────────────────────────────────────────────────────────

export async function get(refObj) {
  return fetchRef(refObj);
}

// ─── onValue (polling-based "realtime") ─────────────────────────────

// Collections that get polled. Others get a single fetch with no auto-refresh.
const POLL_COLLECTIONS = new Set(['notifications', 'wishlists', 'destinations', 'promotedVillas']);

export function onValue(refObj, callback, errorCallback) {
  let cancelled = false;
  let timer;

  const tick = () => {
    if (cancelled) return;
    fetchRef(refObj)
      .then(snapshot => { if (!cancelled) callback(snapshot); })
      .catch(err => {
        console.error('onValue error:', err);
        if (errorCallback) errorCallback(err);
        if (!cancelled) callback(new DataSnapshot(null, refObj.key, false));
      })
      .finally(() => {
        if (!cancelled && POLL_COLLECTIONS.has(refObj.rootName)) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      });
  };

  tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

// ─── set ────────────────────────────────────────────────────────────

export async function set(refObj, value) {
  if (refObj.isWishlistEntry) {
    if (value) {
      try {
        const existing = await apiGet('/wishlists', {
          userId: refObj.wishlistUserId,
          villaId: refObj.wishlistVillaId,
          limit: 1,
        });
        const docs = existing?.documents || [];
        if (docs.length === 0) {
          await apiPost('/wishlists', {
            userId: refObj.wishlistUserId,
            villaId: refObj.wishlistVillaId,
          });
        }
      } catch (err) {
        console.error('Wishlist set error:', err);
        throw err;
      }
    }
    return;
  }

  if (refObj.isAnalytics) return await setAnalytics(refObj, value);

  if (refObj.fieldPath && refObj.docId) {
    const field = refObj.fieldPath.split('/')[0];
    const subField = refObj.fieldPath.split('/').slice(1).join('/');
    try {
      if (subField) {
        // Nested update like users/{uid}/fcmTokens/{token}
        const existing = await apiGet(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`).catch(() => null);
        let fieldData = existing?.[field];
        if (typeof fieldData === 'string') fieldData = jp(fieldData, {});
        if (!fieldData || typeof fieldData !== 'object') fieldData = {};
        fieldData[subField.split('/')[0]] = value;
        if (existing) {
          await apiPut(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`, { [field]: fieldData });
        } else {
          await apiPost(`/${refObj.route}`, { id: refObj.docId, [field]: fieldData });
        }
      } else {
        const payload = { [field]: value };
        try {
          await apiPut(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`, payload);
        } catch {
          await apiPost(`/${refObj.route}`, { id: refObj.docId, ...payload });
        }
      }
    } catch (err) {
      console.error('set fieldPath error:', err);
      throw err;
    }
    return;
  }

  if (refObj.docId) {
    const payload = (value && typeof value === 'object') ? { ...value } : value;
    try {
      await apiPut(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`, payload);
    } catch {
      try {
        await apiPost(`/${refObj.route}`, { id: refObj.docId, ...(payload || {}) });
      } catch (err) {
        console.error('set create error:', err);
        throw err;
      }
    }
    return;
  }

  if (refObj._specificChildId) {
    const existing = await apiGet(`/${refObj.route}`, { ...refObj.filters, limit: MAX });
    const docs = existing?.documents || [];
    const found = docs.find(d => getDocId(d) === refObj._specificChildId || d.bookingId === refObj._specificChildId);
    if (found) {
      await apiPut(`/${refObj.route}/${encodeURIComponent(getDocId(found))}`, value);
    } else {
      await apiPost(`/${refObj.route}`, { id: refObj._specificChildId, ...(value || {}) });
    }
    return;
  }

  // Collection-level set → create new
  await apiPost(`/${refObj.route}`, value || {});
}

async function setAnalytics(refObj, value) {
  if (!refObj.analyticsType || !refObj.analyticsVillaId) return;
  const data = (value && typeof value === 'object')
    ? { ...value, villaId: refObj.analyticsVillaId, type: refObj.analyticsType }
    : { count: value, villaId: refObj.analyticsVillaId, type: refObj.analyticsType };
  try {
    const existing = await apiGet('/analytics', {
      villaId: refObj.analyticsVillaId,
      type: refObj.analyticsType,
      limit: 1,
    });
    const docs = existing?.documents || [];
    if (docs.length > 0) {
      await apiPut(`/analytics/${encodeURIComponent(getDocId(docs[0]))}`, data);
    } else {
      await apiPost('/analytics', data);
    }
  } catch (err) {
    console.error('setAnalytics error:', err);
  }
}

// ─── update ─────────────────────────────────────────────────────────

export async function update(refObj, values) {
  const payload = { ...(values || {}) };

  // Handle increment markers (read-modify-write)
  for (const [key, val] of Object.entries(payload)) {
    if (val && val.__type === 'increment') {
      try {
        if (refObj.docId) {
          const doc = await apiGet(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`);
          payload[key] = (Number(doc?.[key]) || 0) + val.value;
        } else {
          payload[key] = val.value;
        }
      } catch {
        payload[key] = val.value;
      }
    }
  }

  if (refObj.isAnalytics) return await setAnalytics(refObj, payload);

  if (refObj.docId) {
    try {
      await apiPut(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`, payload);
    } catch (err) {
      // Don't auto-create villas on partial updates (would fail validation)
      if (refObj.rootName === 'villas') throw err;
      try {
        await apiPost(`/${refObj.route}`, { id: refObj.docId, ...payload });
      } catch (e) {
        console.error('update create-fallback error:', e);
        throw e;
      }
    }
    return;
  }

  if (refObj._specificChildId) {
    const existing = await apiGet(`/${refObj.route}`, { ...refObj.filters, limit: MAX });
    const docs = existing?.documents || [];
    const found = docs.find(d => getDocId(d) === refObj._specificChildId || d.bookingId === refObj._specificChildId);
    if (found) {
      await apiPut(`/${refObj.route}/${encodeURIComponent(getDocId(found))}`, payload);
    }
    return;
  }

  if (refObj._isAllBookingsLookup) {
    const existing = await apiGet('/bookings', { ...refObj.filters, limit: 1 });
    const docs = existing?.documents || [];
    if (docs.length > 0) {
      await apiPut(`/bookings/${encodeURIComponent(getDocId(docs[0]))}`, payload);
    }
    return;
  }
}

// ─── remove ─────────────────────────────────────────────────────────

export async function remove(refObj) {
  if (refObj.isWishlistEntry) {
    try {
      const existing = await apiGet('/wishlists', {
        userId: refObj.wishlistUserId,
        villaId: refObj.wishlistVillaId,
        limit: 1,
      });
      const docs = existing?.documents || [];
      if (docs.length > 0) {
        await apiDelete(`/wishlists/${encodeURIComponent(getDocId(docs[0]))}`);
      }
    } catch (err) {
      console.error('Wishlist remove error:', err);
      throw err;
    }
    return;
  }

  if (refObj.isAnalytics) {
    if (refObj.analyticsType && refObj.analyticsVillaId) {
      try {
        const existing = await apiGet('/analytics', {
          villaId: refObj.analyticsVillaId,
          type: refObj.analyticsType,
          limit: 1,
        });
        const docs = existing?.documents || [];
        if (docs.length > 0) {
          await apiDelete(`/analytics/${encodeURIComponent(getDocId(docs[0]))}`);
        }
      } catch (err) {
        console.error('Analytics remove error:', err);
      }
    }
    return;
  }

  if (refObj.docId) {
    try {
      await apiDelete(`/${refObj.route}/${encodeURIComponent(refObj.docId)}`);
    } catch (err) {
      console.error('remove error:', err);
    }
    return;
  }

  if (refObj._specificChildId) {
    const existing = await apiGet(`/${refObj.route}`, { ...refObj.filters, limit: MAX });
    const docs = existing?.documents || [];
    const found = docs.find(d => getDocId(d) === refObj._specificChildId || d.bookingId === refObj._specificChildId);
    if (found) await apiDelete(`/${refObj.route}/${encodeURIComponent(getDocId(found))}`);
    return;
  }

  // Bulk delete (rare) — delete all matching documents
  const existing = await apiGet(`/${refObj.route}`, { ...refObj.filters, limit: MAX });
  const docs = existing?.documents || [];
  for (const d of docs) {
    const id = getDocId(d);
    if (id) await apiDelete(`/${refObj.route}/${encodeURIComponent(id)}`);
  }
}

// ─── push ───────────────────────────────────────────────────────────

export function push(refObj) {
  const newId = uniqueId();
  const newRef = new DatabaseRef(`${refObj.path}/${newId}`);
  newRef._isPush = true;
  newRef._pushId = newId;
  if (!newRef.docId) newRef.docId = newId;
  return newRef;
}

// ─── runTransaction ─────────────────────────────────────────────────

export async function runTransaction(refObj, updateFn) {
  const snapshot = await fetchRef(refObj);
  const currentValue = snapshot.val();
  const newValue = updateFn(currentValue);
  if (newValue === undefined) return;
  await set(refObj, newValue);
}

// ─── serverTimestamp & increment ─────────────────────────────────────

export function serverTimestamp() {
  return Date.now();
}

export function increment(n) {
  return { __type: 'increment', value: n };
}

// ─── off (no-op) ────────────────────────────────────────────────────

export function off() {
  // Components should use the unsubscribe function returned by onValue.
}

export function child(refObj, path) {
  return new DatabaseRef(`${refObj.path}/${path}`);
}
